import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, Lock } from "lucide-react";

const KEY = "86d_nav_expanded";
const load = () => { try { return JSON.parse(localStorage.getItem(KEY) || "{}"); } catch { return {}; } };
const ring = "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#e8b4a0]";

function Child({ c, on, Badge }) {
  return (
    <Link to={c.to} aria-current={on ? "page" : undefined} data-testid={`nav-child-${c.id}`}
      className={`flex items-center gap-2 pl-10 pr-3 py-1.5 rounded-lg text-sm transition-colors ${ring} ${on ? "bg-white/10 text-white" : "hover:bg-white/5 hover:text-white"}`}>
      <span className="truncate">{c.label}</span>{c.badge && <Badge />}
      {c.locked && <Lock className="h-3 w-3 ml-auto text-stone-500" aria-label="Requires a plan upgrade" />}
    </Link>
  );
}

function Section({ s, ws, open, onToggle, Badge }) {
  const on = ws?.section.id === s.id;
  const multi = s.children.length > 1;
  return (
    <div data-testid={`nav-section-${s.id}`}>
      <div className={`flex items-center rounded-lg transition-colors ${on ? "text-white" : "hover:bg-white/5"} ${on && !open ? "bg-white/10" : ""}`}>
        <Link to={s.to} aria-current={on && !multi ? "page" : undefined} data-testid={`nav-${s.id}`}
          className={`flex-1 min-w-0 flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium hover:text-white ${ring}`}>
          <s.icon className="h-4 w-4 shrink-0" /><span className="truncate">{s.label}</span>
          {!open && s.children.some((c) => c.badge) && <Badge />}
        </Link>
        {multi && (
          <button type="button" onClick={onToggle} aria-expanded={open} aria-controls={`nav-children-${s.id}`} aria-label={`${open ? "Collapse" : "Expand"} ${s.label}`}
            data-testid={`nav-toggle-${s.id}`} className={`h-8 w-8 mr-1 shrink-0 rounded-md flex items-center justify-center hover:bg-white/10 hover:text-white ${ring}`}>
            <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
          </button>
        )}
      </div>
      {multi && open && (
        <div id={`nav-children-${s.id}`} role="group" aria-label={s.label} className="mt-0.5 mb-1 space-y-0.5" data-testid={`nav-children-${s.id}`}>
          {s.children.map((c) => <Child key={c.id} c={c} on={ws?.child.id === c.id} Badge={Badge} />)}
        </div>
      )}
    </div>
  );
}

export default function SidebarNav({ sections, ws, Badge }) {
  const [manual, setManual] = useState(load);
  const [shut, setShut] = useState(null);
  const aid = ws?.section.id;
  useEffect(() => { setShut(null); }, [aid]);
  const isOpen = (id) => (id === aid ? shut !== id : !!manual[id]);
  const toggle = (id) => {
    if (id === aid) { setShut((x) => (x === id ? null : id)); return; }
    setManual((m) => { const n = { ...m, [id]: !m[id] }; localStorage.setItem(KEY, JSON.stringify(n)); return n; });
  };
  const render = (s) => <Section key={s.id} s={s} ws={ws} open={isOpen(s.id)} onToggle={() => toggle(s.id)} Badge={Badge} />;
  const util = sections.filter((s) => s.utility);
  return (
    <>
      <div className="mt-2 space-y-0.5" data-testid="nav-main">{sections.filter((s) => !s.utility).map(render)}</div>
      {!!util.length && <div className="mt-4 pt-4 border-t border-white/10 space-y-0.5" data-testid="nav-utility">{util.map(render)}</div>}
    </>
  );
}
